import React, { useState } from 'react';
import { Calendar, AlertCircle, Link } from 'lucide-react';

export type MilestoneType = 
  | 'draft_progress'  // For word count and draft completion
  | 'feedback'        // For beta reading and feedback collection
  | 'revision'        // For editing and revision cycles
  | 'publishing'      // For publication steps
  | 'custom';         // For any other milestone type

export type MilestoneStatus = 'planned' | 'in_progress' | 'completed';

interface CreateMilestoneFormProps {
  onSubmit: (data: {
    title: string;
    description: string | null;
    type: MilestoneType;
    status: MilestoneStatus;
    targetDate: string | null;
    wordCount?: number | null;
    betaReaderCount?: number | null;
    customMetrics?: Record<string, string | number> | null;
    purchaseLink?: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}

export default function CreateMilestoneForm({
  onSubmit,
  onCancel
}: CreateMilestoneFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<MilestoneType>('draft_progress');
  const [status, setStatus] = useState<MilestoneStatus>('planned');
  const [targetDate, setTargetDate] = useState('');
  const [wordCount, setWordCount] = useState<number | undefined>();
  const [betaReaderCount, setBetaReaderCount] = useState<number | undefined>();
  const [purchaseLink, setPurchaseLink] = useState('');
  const [customMetricKey, setCustomMetricKey] = useState('');
  const [customMetricValue, setCustomMetricValue] = useState('');
  const [customMetrics, setCustomMetrics] = useState<Record<string, string | number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      const data = {
        title,
        description: description || null,
        type,
        status,
        targetDate: targetDate || null,
        wordCount: type === 'draft_progress' ? wordCount || null : null,
        betaReaderCount: type === 'feedback' ? betaReaderCount || null : null,
        customMetrics: type === 'custom' ? customMetrics : null,
        purchaseLink: type === 'publishing' ? purchaseLink || null : null
      };

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create milestone');
      throw err; // Re-throw to let parent component handle errors if needed
    } finally {
      setLoading(false);
    }
  };

  const addCustomMetric = () => {
    if (!customMetricKey.trim()) return;
    
    setCustomMetrics(prev => ({
      ...prev,
      [customMetricKey.trim()]: customMetricValue
    }));
    setCustomMetricKey('');
    setCustomMetricValue('');
  };

  const removeCustomMetric = (key: string) => {
    setCustomMetrics(prev => {
      const newMetrics = { ...prev };
      delete newMetrics[key];
      return newMetrics;
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div className="text-red-700">{error}</div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Type
        </label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as MilestoneType)}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        >
          <option value="draft_progress">Draft Progress</option>
          <option value="feedback">Feedback & Beta Reading</option>
          <option value="revision">Revision & Editing</option>
          <option value="publishing">Publishing</option>
          <option value="custom">Custom</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as MilestoneStatus)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Target Date
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full pl-10 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Type-specific fields */}
      {type === 'draft_progress' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Word Count Goal
          </label>
          <input
            type="number"
            value={wordCount || ''}
            onChange={(e) => setWordCount(parseInt(e.target.value) || undefined)}
            min={1}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      )}

      {type === 'feedback' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Number of Beta Readers
          </label>
          <input
            type="number"
            value={betaReaderCount || ''}
            onChange={(e) => setBetaReaderCount(parseInt(e.target.value) || undefined)}
            min={1}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
      )}

      {type === 'publishing' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Purchase Link
          </label>
          <div className="relative">
            <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="url"
              value={purchaseLink}
              onChange={(e) => setPurchaseLink(e.target.value)}
              placeholder="https://..."
              className="w-full pl-10 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Add a link where readers can purchase or pre-order your book
          </p>
        </div>
      )}

      {type === 'custom' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metric Name
              </label>
              <input
                type="text"
                value={customMetricKey}
                onChange={(e) => setCustomMetricKey(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="e.g., Chapters Completed"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Value
              </label>
              <input
                type="text"
                value={customMetricValue}
                onChange={(e) => setCustomMetricValue(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="e.g., 5"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={addCustomMetric}
                disabled={!customMetricKey.trim()}
                className="p-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>

          {Object.entries(customMetrics).length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Custom Metrics
              </label>
              <div className="space-y-2">
                {Object.entries(customMetrics).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">{key}:</span> {value}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeCustomMetric(key)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 hover:text-gray-900"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading || !title}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {loading ? 'Creating...' : 'Create Milestone'}
        </button>
      </div>
    </form>
  );
}